import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link, useNavigate, useLocation } from "react-router-dom";
import Webcam from "react-webcam";
import confetti from "canvas-confetti";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Dialog, DialogContent, DialogDescription, DialogFooter, 
  DialogHeader, DialogTitle, DialogTrigger 
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { 
  ArrowLeft, Loader2, Save, Trash2, 
  Package, ShieldAlert, ShoppingCart, Trash, Copy, CheckCircle,
  Camera, ImagePlus, X, ZoomIn, ZoomOut, RotateCcw, History, 
  Clock, User, RefreshCw, Edit, PlusCircle, MoreVertical, FileX, PartyPopper
} from "lucide-react";
import { toast } from "sonner";
import { getAuthHeaders } from "@/utils/auth";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const STATUS_OPTIONS = ["Active", "Stolen", "Sold", "Discarded", "Written Off"];

const STATUS_STYLES = {
  Active: "status-active",
  Stolen: "status-stolen",
  Sold: "status-sold",
  Discarded: "status-discarded",
  "Written Off": "status-discarded"
};

const STATUS_ICONS = {
  Active: Package,
  Stolen: ShieldAlert,
  Sold: ShoppingCart,
  Discarded: Trash,
  "Written Off": FileX
};

export default function ToolDetail() {
  const { toolId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const webcamRef = useRef(null);
  const photoInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  
  const [tool, setTool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [status, setStatus] = useState("");
  const [description, setDescription] = useState("");
  const [estimatedValue, setEstimatedValue] = useState("");
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [showRecoverDialog, setShowRecoverDialog] = useState(false);
  const [recoveryNotes, setRecoveryNotes] = useState("");
  const [recovering, setRecovering] = useState(false);
  const [refreshingPrice, setRefreshingPrice] = useState(false);
  const [refreshesRemaining, setRefreshesRemaining] = useState(null);
  const [activeImageIdx, setActiveImageIdx] = useState(0);
  
  // Determine back link based on where user came from
  const fromAdminTools = location.state?.from === 'admin';
  const fromAdminUsers = location.state?.from === 'admin-users';
  
  let backLink = "/dashboard";
  let backLabel = "Back";
  
  if (fromAdminTools) {
    backLink = "/admin/tools";
    backLabel = "Tool Oversight";
  } else if (fromAdminUsers) {
    backLink = "/admin/users";
    backLabel = "User Management";
  }

  useEffect(() => {
    fetchTool();
    fetchRefreshesRemaining();
  }, [toolId]);

  const fetchRefreshesRemaining = async () => {
    try {
      const response = await fetch(`${API}/tools/refresh-price/remaining`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setRefreshesRemaining(data.remaining);
      }
    } catch {}
  };

  const fetchTool = async () => {
    try {
      const response = await fetch(`${API}/tools/${toolId}`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        if (response.status === 404) {
          toast.error("Tool not found");
          navigate("/dashboard");
          return;
        }
        throw new Error("Failed to fetch tool");
      }

      const data = await response.json();
      setTool(data);
      setStatus(data.status);
      setDescription(data.description || "");
      setEstimatedValue(data.estimated_value || "");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const response = await fetch(`${API}/tools/${toolId}/history`, {
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error("Failed to fetch history");
      }

      const data = await response.json();
      setHistory(data.history || []);
    } catch (error) {
      toast.error("Could not load tool history");
    } finally {
      setHistoryLoading(false);
    }
  };

  const toggleHistory = () => {
    if (!showHistory && history.length === 0) {
      fetchHistory();
    }
    setShowHistory(!showHistory);
  };

  const formatEventType = (type) => {
    const types = {
      created: { label: "Tool Registered", icon: PlusCircle, color: "text-green-400" },
      status_changed: { label: "Status Changed", icon: RefreshCw, color: "text-yellow-400" },
      details_updated: { label: "Details Updated", icon: Edit, color: "text-blue-400" },
      ownership_transferred: { label: "Ownership Transferred", icon: User, color: "text-purple-400" },
      image_updated: { label: "Image Updated", icon: ImagePlus, color: "text-cyan-400" }
    };
    return types[type] || { label: type, icon: Clock, color: "text-muted-foreground" };
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Haptic feedback helper - with iOS fallback using AudioContext
  const triggerHaptic = useCallback((pattern = [50]) => {
    // Try standard Vibration API first (Android, some browsers)
    if ('vibrate' in navigator) {
      try {
        navigator.vibrate(pattern);
        return;
      } catch (e) {
        // Vibration failed, try fallback
      }
    }
    
    // iOS fallback: Create a subtle audio "tick" using AudioContext
    try {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        const audioCtx = new AudioContextClass();
        const oscillator = audioCtx.createOscillator();
        const gainNode = audioCtx.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioCtx.destination);
        
        oscillator.frequency.value = 150;
        oscillator.type = 'sine';
        
        const duration = Math.min(pattern[0] || 50, 30) / 1000;
        gainNode.gain.setValueAtTime(0, audioCtx.currentTime);
        gainNode.gain.linearRampToValueAtTime(0.1, audioCtx.currentTime + duration / 2);
        gainNode.gain.linearRampToValueAtTime(0, audioCtx.currentTime + duration);
        
        oscillator.start(audioCtx.currentTime);
        oscillator.stop(audioCtx.currentTime + duration);
        
        setTimeout(() => audioCtx.close(), 100);
      }
    } catch (e) {
      // Audio feedback not available, fail silently
    }
  }, []);

  const handleSave = async (newImageUrl = null, newLabelImageUrl = undefined) => {
    setSaving(true);
    
    try {
      const updateData = { status, description };
      if (newImageUrl !== null) {
        updateData.image_url = newImageUrl;
      }
      if (newLabelImageUrl !== undefined) {
        updateData.label_image_url = newLabelImageUrl;
      }
      // Include estimated_value if changed
      const valueNum = estimatedValue ? parseFloat(estimatedValue) : null;
      if (valueNum !== tool.estimated_value) {
        updateData.estimated_value = valueNum;
      }

      // Get session token from localStorage for iOS Safari fallback
      const storedToken = localStorage.getItem('session_token');
      const headers = { "Content-Type": "application/json" };
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }

      // Use admin endpoint if user came from admin area (to update other users' tools)
      const isAdminContext = fromAdminTools || fromAdminUsers;
      const endpoint = isAdminContext ? `${API}/admin/tools/${toolId}` : `${API}/tools/${toolId}`;

      const response = await fetch(endpoint, {
        method: "PUT",
        headers,
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to update tool");
      }

      const updated = await response.json();
      setTool(updated);
      
      // Haptic feedback based on status
      if (status === "Stolen") {
        triggerHaptic([100, 100, 100]); // Alert pattern for stolen
      } else {
        triggerHaptic([20, 30, 50]); // Success pattern
      }
      toast.success("Tool updated successfully!");
      navigate("/dashboard");
    } catch (error) {
      triggerHaptic([100, 50, 100]); // Error pattern
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    
    try {
      // Use admin endpoint if user came from admin area
      const isAdminContext = fromAdminTools || fromAdminUsers;
      const endpoint = isAdminContext ? `${API}/admin/tools/${toolId}` : `${API}/tools/${toolId}`;
      
      const response = await fetch(endpoint, {
        method: "DELETE",
        headers: getAuthHeaders()
      });

      if (!response.ok) {
        throw new Error("Failed to delete tool");
      }

      triggerHaptic([50, 100]); // Deletion confirmation
      toast.success("Tool deleted");
      
      // Navigate back to appropriate page
      navigate(isAdminContext ? backLink : "/dashboard");
    } catch (error) {
      triggerHaptic([100, 50, 100]); // Error pattern
      toast.error(error.message);
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleRecover = async () => {
    setRecovering(true);
    
    try {
      const response = await fetch(`${API}/tools/${toolId}/recover`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders()
        },
        body: JSON.stringify({ recovery_notes: recoveryNotes || null })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Failed to mark tool as recovered");
      }

      const updated = await response.json();
      setTool(updated);
      setStatus(updated.status);
      
      triggerHaptic([50, 30, 50, 30, 100]); // Celebration pattern
      
      // Confetti celebration
      const burst = (opts) => confetti({ ...opts, disableForReducedMotion: true });
      burst({ particleCount: 80, spread: 70, origin: { y: 0.6 } });
      setTimeout(() => burst({ particleCount: 50, angle: 60, spread: 55, origin: { x: 0 } }), 250);
      setTimeout(() => burst({ particleCount: 50, angle: 120, spread: 55, origin: { x: 1 } }), 400);
      
      toast.success("Tool marked as recovered! Great news!");
      setShowRecoverDialog(false);
      setRecoveryNotes("");
    } catch (error) {
      triggerHaptic([100, 50, 100]); // Error pattern
      toast.error(error.message);
    } finally {
      setRecovering(false);
    }
  };

  const copyToolId = () => {
    navigator.clipboard.writeText(tool.tool_id);
    triggerHaptic([30]); // Quick tap for copy
    setCopied(true);
    toast.success("Tool ID copied!");
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRefreshPrice = async () => {
    if (refreshesRemaining !== null && refreshesRemaining <= 0) {
      toast.error("Daily price refresh limit reached (2 per day). Try again tomorrow.");
      return;
    }
    setRefreshingPrice(true);
    try {
      const storedToken = localStorage.getItem('session_token');
      const headers = { "Content-Type": "application/json" };
      if (storedToken) headers['Authorization'] = `Bearer ${storedToken}`;

      const response = await fetch(`${API}/tools/${toolId}/refresh-price`, {
        method: "POST",
        headers,
      });

      if (response.status === 429) {
        const data = await response.json().catch(() => ({}));
        setRefreshesRemaining(0);
        throw new Error(data.detail || "Daily limit reached. Try again tomorrow.");
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || "Failed to refresh price");
      }

      const updated = await response.json();
      setTool(updated);
      setRefreshesRemaining(prev => prev !== null ? Math.max(0, prev - 1) : null);
      triggerHaptic([20, 30, 50]);
      toast.success(`Price updated: £${updated.scan_price?.toFixed(2)} (Retail)`);
    } catch (error) {
      triggerHaptic([100, 50, 100]);
      toast.error(error.message);
    } finally {
      setRefreshingPrice(false);
    }
  };

  const capturePhoto = async () => {
    if (!webcamRef.current) return;
    
    setUploadingPhoto(true);
    const imageSrc = webcamRef.current.getScreenshot();
    
    if (!imageSrc) {
      toast.error("Failed to capture photo");
      setUploadingPhoto(false);
      return;
    }

    const response = await fetch(imageSrc);
    const blob = await response.blob();
    
    await uploadPhoto(blob);
    setShowCamera(false);
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    
    setUploadingPhoto(true);
    await uploadPhoto(file);
  };

  const uploadPhoto = async (imageBlob) => {
    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", imageBlob, "tool_photo.jpg");

      const storedToken = localStorage.getItem('session_token');
      const headers = {};
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }

      const response = await fetch(`${API}/tools/upload-image`, {
        method: "POST",
        headers,
        body: uploadFormData
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const result = await response.json();
      
      // Reset image error state when new image is uploaded
      setImgError(false);
      
      // Image priority: user photo is always primary, label scan becomes secondary
      // If the current primary image_url was the label (no label_image_url set),
      // move it to label_image_url so it's preserved
      const currentLabel = tool.label_image_url;
      const currentImage = tool.image_url;
      
      if (!currentLabel && currentImage) {
        // Current image_url is likely the label scan (no separate label stored yet)
        // Move it to label_image_url, set new photo as primary
        await handleSave(result.image_url, currentImage);
      } else {
        // Label already stored separately, just update the primary
        await handleSave(result.image_url, currentLabel);
      }
      toast.success("Photo updated!");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const removePhoto = async () => {
    // Remove the user tool photo only — if label exists, it becomes primary again
    const currentLabel = tool.label_image_url;
    if (currentLabel) {
      // Label exists as secondary — promote it back to primary, clear label field
      await handleSave(currentLabel, null);
    } else {
      // No label — just clear the image
      await handleSave("", null);
    }
  };

  const openImageViewer = () => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
    setShowImageViewer(true);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.25, 5));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.25, 1));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  // Mouse events for desktop
  const handleMouseDown = (e) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - panPosition.x, y: e.clientY - panPosition.y });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setPanPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch events for mobile
  const handleTouchStart = (e) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      const touch = e.touches[0];
      setDragStart({ x: touch.clientX - panPosition.x, y: touch.clientY - panPosition.y });
    }
  };

  const handleTouchMove = (e) => {
    if (isDragging && e.touches.length === 1) {
      e.preventDefault();
      const touch = e.touches[0];
      setPanPosition({
        x: touch.clientX - dragStart.x,
        y: touch.clientY - dragStart.y
      });
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoomLevel(prev => Math.min(prev + 0.25, 5));
    } else {
      setZoomLevel(prev => {
        const newZoom = Math.max(prev - 0.25, 1);
        return newZoom;
      });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin" />
      </div>
    );
  }

  if (!tool) {
    return null;
  }

  const StatusIcon = STATUS_ICONS[tool.status] || Package;
  const hasChanges = status !== tool.status || description !== (tool.description || "") || (estimatedValue || "") !== (tool.estimated_value?.toString() || "");
  const imageUrl = tool.image_url ? (tool.image_url.startsWith('/api') ? process.env.REACT_APP_BACKEND_URL + tool.image_url : tool.image_url) : null;
  const labelImageUrl = tool.label_image_url ? (tool.label_image_url.startsWith('/api') ? process.env.REACT_APP_BACKEND_URL + tool.label_image_url : tool.label_image_url) : null;
  // Collect all available images for gallery display
  const toolImages = [
    imageUrl && { url: imageUrl, label: "Tool Photo" },
    labelImageUrl && { url: labelImageUrl, label: "Label Scan" }
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-3 lg:px-8">
          <div className="flex items-center gap-2 md:gap-4">
            <Button variant="ghost" size="sm" className="h-10 md:h-8 px-2 md:px-3" data-testid="back-btn" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-5 h-5 md:w-4 md:h-4" />
              <span className="hidden md:inline ml-2">{backLabel}</span>
            </Button>
            <div className="flex items-center gap-2">
              <Package className="w-5 h-5 md:w-6 md:h-6 text-primary hidden md:block" />
              <span className="font-heading text-lg md:text-xl font-bold uppercase">Tool Details</span>
            </div>
          </div>
          
          {/* Desktop Delete Button */}
          <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
            <DialogTrigger asChild>
              <Button variant="destructive" size="sm" className="hidden md:flex" data-testid="delete-tool-btn">
                <Trash2 className="w-4 h-4 mr-2" />
                Delete
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader>
                <DialogTitle className="font-heading uppercase">Delete Tool</DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this tool? This action cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDeleteDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={handleDelete}
                  disabled={deleting}
                  data-testid="confirm-delete-btn"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          {/* Mobile More Options */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="sm" className="md:hidden h-10 px-2" data-testid="mobile-more-options">
                <MoreVertical className="w-5 h-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="bg-[#0f172a] border-t border-yellow-500/20 rounded-t-2xl">
              <SheetHeader>
                <SheetTitle className="text-white text-left">Options</SheetTitle>
              </SheetHeader>
              <div className="py-4 space-y-3">
                <Button 
                  variant="ghost" 
                  className="w-full justify-start h-14 text-base"
                  onClick={toggleHistory}
                >
                  <History className="w-5 h-5 mr-3 text-primary" />
                  View History
                </Button>
                <Button 
                  variant="ghost" 
                  className="w-full justify-start h-14 text-base text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  <Trash2 className="w-5 h-5 mr-3" />
                  Delete Tool
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      <main className="p-4 lg:p-8 max-w-3xl mx-auto">
        {/* Mobile Hero Image Gallery */}
        <div className="md:hidden -mx-4 -mt-4 mb-4">
          {toolImages.length > 0 && !imgError ? (
            <div className="relative">
              <div className="relative h-48 bg-black/20" onClick={openImageViewer}>
                <img 
                  src={toolImages[activeImageIdx]?.url || imageUrl} 
                  alt={`${tool.manufacturer} ${tool.tool_type}`}
                  className="w-full h-full object-cover"
                  onError={() => setImgError(true)}
                />
                <div className="absolute bottom-2 right-2 flex gap-2">
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    className="h-10 opacity-90"
                    onClick={(e) => { e.stopPropagation(); cameraInputRef.current?.click(); }}
                  >
                    <Camera className="w-4 h-4 mr-2" /> Take
                  </Button>
                  <Button 
                    size="sm" 
                    variant="secondary" 
                    className="h-10 opacity-90"
                    onClick={(e) => { e.stopPropagation(); photoInputRef.current?.click(); }}
                  >
                    <ImagePlus className="w-4 h-4 mr-2" /> Upload
                  </Button>
                </div>
                {/* Status Badge Overlay */}
                <Badge className={`absolute top-3 left-3 ${STATUS_STYLES[tool.status]} border font-mono text-sm uppercase tracking-wider px-3 py-1`}>
                  <StatusIcon className="w-4 h-4 mr-2" />
                  {tool.status}
                </Badge>
              </div>
              {/* Image thumbnails - only show if 2 images */}
              {toolImages.length > 1 && (
                <div className="flex gap-2 px-3 py-2 bg-card/80" data-testid="mobile-image-tabs">
                  {toolImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImageIdx(idx)}
                      className={`flex-1 h-14 rounded-md overflow-hidden border-2 transition-colors ${activeImageIdx === idx ? "border-primary" : "border-transparent opacity-60"}`}
                      data-testid={`mobile-img-tab-${idx}`}
                    >
                      <img src={img.url} alt={img.label} className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="h-48 bg-secondary/20 flex flex-col items-center justify-center gap-4">
              <ImagePlus className="w-12 h-12 text-muted-foreground/30" />
              <div className="flex gap-3">
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => cameraInputRef.current?.click()}
                  className="h-12 px-4"
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                  ) : (
                    <><Camera className="w-4 h-4 mr-2" /> Take Photo</>
                  )}
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  onClick={() => photoInputRef.current?.click()}
                  className="h-12 px-4"
                  disabled={uploadingPhoto}
                >
                  <ImagePlus className="w-4 h-4 mr-2" /> Upload
                </Button>
              </div>
            </div>
          )}
        </div>
        
        {/* Hidden file input for camera capture (mobile) */}
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={handlePhotoUpload}
          className="hidden"
        />
        
        {/* Hidden file input for photo upload (gallery) */}
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          onChange={handlePhotoUpload}
          className="hidden"
        />

        {/* Desktop Tool Photo Section */}
        <Card className="border-border bg-card mb-4 md:mb-6 hidden md:block">
          <CardContent className="p-4">
            <Label className="text-sm text-muted-foreground mb-3 block">Tool Images</Label>
            {showCamera ? (
              <div>
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  screenshotFormat="image/jpeg"
                  videoConstraints={{ facingMode: "environment" }}
                  className="w-full max-w-md rounded border border-border"
                />
                <div className="flex gap-2 mt-3">
                  <Button 
                    size="sm"
                    onClick={capturePhoto} 
                    disabled={uploadingPhoto}
                  >
                    {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Camera className="w-4 h-4 mr-1" />}
                    Capture
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setShowCamera(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : toolImages.length > 0 && !imgError ? (
              <div className="flex items-start gap-4">
                <div className="flex gap-3">
                  {toolImages.map((img, idx) => (
                    <div key={idx} className="relative group">
                      <div 
                        className={`w-40 h-40 flex-shrink-0 rounded border-2 bg-black/10 overflow-hidden cursor-pointer transition-colors ${
                          activeImageIdx === idx ? "border-primary" : "border-border hover:border-primary/50"
                        }`}
                        onClick={() => { setActiveImageIdx(idx); openImageViewer(); }}
                        title={`${img.label} — Click to enlarge`}
                        data-testid={`desktop-img-${idx}`}
                      >
                        <img 
                          src={img.url} 
                          alt={img.label}
                          className="w-full h-full object-cover"
                          onError={() => setImgError(true)}
                        />
                      </div>
                      <span className="absolute bottom-1 left-1 text-[10px] font-bold bg-black/70 text-white px-1.5 py-0.5 rounded">
                        {img.label}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-col gap-2">
                  {toolImages.length < 2 && (
                    <>
                      <Button size="sm" variant="outline" className="justify-start" onClick={() => setShowCamera(true)} disabled={uploadingPhoto}>
                        <Camera className="w-4 h-4 mr-2" /> Take New
                      </Button>
                      <Button size="sm" variant="outline" className="justify-start" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto}>
                        {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                        Upload
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" className="justify-start text-destructive hover:text-destructive" onClick={removePhoto} disabled={uploadingPhoto}>
                    <X className="w-4 h-4 mr-2" /> Remove Photo
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-40 h-40 flex-shrink-0 rounded border-2 border-dashed border-border bg-secondary/10 flex items-center justify-center">
                  <ImagePlus className="w-8 h-8 text-muted-foreground/50" />
                </div>
                <div className="flex flex-col gap-2">
                  <Button size="sm" variant="outline" className="justify-start" onClick={() => setShowCamera(true)} disabled={uploadingPhoto}>
                    <Camera className="w-4 h-4 mr-2" /> Take Photo
                  </Button>
                  <Button size="sm" variant="outline" className="justify-start" onClick={() => photoInputRef.current?.click()} disabled={uploadingPhoto}>
                    {uploadingPhoto ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ImagePlus className="w-4 h-4 mr-2" />}
                    Upload Photo
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tool Info Card */}
        <Card className="border-border bg-card mb-4 md:mb-6">
          <CardHeader className="pb-3 md:pb-4">
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="font-heading text-3xl md:text-4xl uppercase mb-1 md:mb-2">
                  {tool.manufacturer}
                </CardTitle>
                <p className="text-primary text-xl md:text-2xl font-semibold capitalize">{tool.tool_type}</p>
              </div>
              {/* Desktop Status Badge */}
              <Badge className={`hidden md:flex ${STATUS_STYLES[tool.status]} border font-mono text-sm uppercase tracking-wider px-3 py-1`}>
                <StatusIcon className="w-4 h-4 mr-2" />
                {tool.status}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 md:space-y-6">
            {/* Tool ID */}
            <div className="p-3 md:p-4 bg-secondary/30 rounded-xl md:rounded border border-border">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Unique Tool ID</p>
                  <p className="font-mono text-lg md:text-xl text-primary truncate">{tool.tool_id}</p>
                </div>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={copyToolId}
                  className="h-10 md:h-8 ml-2 shrink-0"
                  data-testid="copy-tool-id-btn"
                >
                  {copied ? <CheckCircle className="w-5 h-5 md:w-4 md:h-4 text-green-500" /> : <Copy className="w-5 h-5 md:w-4 md:h-4" />}
                </Button>
              </div>
            </div>

            {/* Details Grid - Stack on mobile */}
            <div className="grid grid-cols-2 gap-3 md:gap-4">
              <div className="p-3 md:p-4 bg-secondary/20 rounded-xl md:rounded border border-border">
                <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Serial Number</p>
                <p className="font-mono text-base md:text-lg truncate">{tool.serial_number}</p>
              </div>
              {tool.model_number && (
                <div className="p-3 md:p-4 bg-secondary/20 rounded-xl md:rounded border border-border">
                  <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Model Number</p>
                  <p className="font-mono text-base md:text-lg truncate">{tool.model_number}</p>
                </div>
              )}
              <div className="p-3 md:p-4 bg-secondary/20 rounded-xl md:rounded border border-border">
                <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Added</p>
                <p className="text-base">{new Date(tool.created_at).toLocaleDateString()}</p>
              </div>
              <div className="p-3 md:p-4 bg-secondary/20 rounded-xl md:rounded border border-border">
                <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Last Updated</p>
                <p className="text-base">{new Date(tool.updated_at).toLocaleDateString()}</p>
              </div>
              {tool.estimated_value && (
                <div className="p-3 md:p-4 bg-secondary/20 rounded-xl md:rounded border border-border col-span-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-sm text-muted-foreground uppercase tracking-wide">Value</p>
                    <p className="font-mono text-base md:text-lg font-medium text-primary">£{tool.estimated_value.toFixed(2)}</p>
                    {(() => {
                      const isUserEdited = tool.scan_price && Math.abs(tool.estimated_value - tool.scan_price) > 0.01;
                      const source = isUserEdited ? 'User' : (tool.scan_price_source || tool.price_source || 'Estimated');
                      const pillClass = source === 'Retail'
                        ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                        : source === 'User'
                          ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                          : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
                      return (
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${pillClass}`} data-testid="tool-price-source-badge">
                          {source}
                        </span>
                      );
                    })()}
                  </div>
                  {tool.scan_price && Math.abs(tool.estimated_value - tool.scan_price) > 0.01 && (
                    <p className="text-xs text-muted-foreground mt-1">
                      TOOLIO scan: <span className="font-mono">£{tool.scan_price.toFixed(2)}</span>
                      <span className={`ml-1 ${tool.scan_price_source === 'Retail' ? 'text-green-400' : 'text-yellow-400'}`}>
                        ({tool.scan_price_source || 'Estimated'})
                      </span>
                    </p>
                  )}
                </div>
              )}
              {!tool.estimated_value && (
                <div className="p-3 md:p-4 bg-secondary/20 rounded-xl md:rounded border border-border col-span-2">
                  <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Value</p>
                  <p className="text-base text-muted-foreground">No value recorded</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Case Number - shown for Stolen tools */}
        {tool.case_number && tool.status === "Stolen" && (
          <Card className="border-red-500/30 bg-red-500/5 mb-4 md:mb-6" data-testid="case-number-card">
            <CardContent className="p-3 md:p-4">
              <p className="text-sm text-muted-foreground uppercase tracking-wide mb-1">Case Reference</p>
              <p className="font-mono text-base md:text-lg text-red-400 font-medium">{tool.case_number}</p>
            </CardContent>
          </Card>
        )}

        {/* Edit Card */}
        <Card className="border-border bg-card mb-4 md:mb-6">
          <CardHeader className="pb-2 md:pb-4">
            <CardTitle className="font-heading uppercase text-xl md:text-2xl">Update Tool</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label className="text-base">Status</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger className="bg-background border-input h-14 md:h-10" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(s => {
                    const Icon = STATUS_ICONS[s];
                    // "Written Off" only available if tool is currently Stolen
                    const isWrittenOffDisabled = s === "Written Off" && tool?.status !== "Stolen";
                    return (
                      <SelectItem 
                        key={s} 
                        value={s}
                        disabled={isWrittenOffDisabled}
                        className={isWrittenOffDisabled ? "opacity-50" : ""}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4" />
                          {s}
                          {isWrittenOffDisabled && (
                            <span className="text-xs text-muted-foreground ml-1">(requires Stolen)</span>
                          )}
                        </div>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              
              {status === "Stolen" && (
                <p className="text-sm text-red-400 mt-2">
                  ⚠️ Marking as stolen will flag this tool. Other users cannot register it.
                </p>
              )}
              {(status === "Sold" || status === "Discarded") && (
                <p className="text-sm text-muted-foreground mt-2">
                  ℹ️ Marking as {status.toLowerCase()} allows others to register this tool.
                </p>
              )}
              {status === "Written Off" && (
                <p className="text-sm text-amber-400 mt-2">
                  📋 This tool has been written off (unrecovered). It will be excluded from your inventory value.
                </p>
              )}
              
              {/* Mark as Recovered Button - Only for Stolen tools */}
              {tool?.status === "Stolen" && (
                <Button 
                  className="w-full mt-4 bg-green-600 hover:bg-green-700 text-white h-14 md:h-10 text-base md:text-sm"
                  onClick={() => setShowRecoverDialog(true)}
                  data-testid="mark-recovered-btn"
                >
                  <PartyPopper className="w-5 h-5 md:w-4 md:h-4 mr-2" />
                  Mark as Recovered
                </Button>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-base">Notes / Description</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add notes about this tool..."
                className="bg-background border-input min-h-[100px]"
                rows={4}
                data-testid="description-textarea"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimated-value" className="text-base">Estimated Value (£)</Label>
              <Input
                id="estimated-value"
                type="number"
                step="0.01"
                min="0"
                value={estimatedValue}
                onChange={(e) => setEstimatedValue(e.target.value)}
                placeholder="e.g., 299.99"
                className="bg-background border-input h-14 md:h-10"
                data-testid="estimated-value-input"
              />
              <p className="text-sm text-muted-foreground">For insurance purposes. Having a value recorded helps speed up claims if your tool is ever stolen.</p>
              {/* Show locked scan price if user has changed the value */}
              {tool.scan_price && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/30 rounded px-3 py-3 border border-border" data-testid="scan-price-locked">
                  <span>TOOLIO scan price:</span>
                  <span className="font-mono font-medium text-foreground text-base">£{tool.scan_price.toFixed(2)}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    tool.scan_price_source === 'Retail' 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-yellow-500/20 text-yellow-400'
                  }`}>{tool.scan_price_source || 'Estimated'}</span>
                  <span className="opacity-60">locked</span>
                  <button
                    onClick={handleRefreshPrice}
                    disabled={refreshingPrice || refreshesRemaining === 0}
                    className="ml-auto text-sm px-2.5 py-1 rounded-full bg-primary/10 text-primary hover:bg-primary/20 border border-primary/20 transition-all flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="edit-refresh-price-btn"
                    title={refreshesRemaining === 0 ? 'Daily limit reached' : `${refreshesRemaining} refreshes left today`}
                  >
                    <RefreshCw className={`w-3 h-3 ${refreshingPrice ? 'animate-spin' : ''}`} />
                    {refreshingPrice ? 'Checking...' : refreshesRemaining === 0 ? 'Limit reached' : `Refresh${refreshesRemaining !== null ? ` (${refreshesRemaining}/2)` : ''}`}
                  </button>
                </div>
              )}
            </div>

            <Button 
              className="w-full btn-primary h-14 md:h-10 text-base md:text-sm" 
              onClick={() => handleSave()}
              disabled={saving || !hasChanges}
              data-testid="save-changes-btn"
            >
              {saving ? (
                <><Loader2 className="w-5 h-5 md:w-4 md:h-4 mr-2 animate-spin" /> Saving...</>
              ) : (
                <><Save className="w-5 h-5 md:w-4 md:h-4 mr-2" /> Save Changes</>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Tool History Card */}
        <Card className="border-border bg-card mb-6">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-heading text-xl md:text-2xl uppercase flex items-center gap-2">
                <History className="w-5 h-5 text-primary" />
                Tool History
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={toggleHistory}
                data-testid="toggle-history-btn"
              >
                {showHistory ? "Hide" : "Show"} History
              </Button>
            </div>
          </CardHeader>
          
          {showHistory && (
            <CardContent>
              {historyLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 text-primary animate-spin" />
                </div>
              ) : history.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Clock className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No history recorded yet</p>
                </div>
              ) : (
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />
                  
                  <div className="space-y-4">
                    {history.map((event, index) => {
                      const eventInfo = formatEventType(event.event_type);
                      const EventIcon = eventInfo.icon;
                      
                      return (
                        <div key={event.event_id || index} className="relative pl-10">
                          {/* Timeline dot */}
                          <div className={`absolute left-2 w-5 h-5 rounded-full bg-card border-2 border-border flex items-center justify-center ${eventInfo.color}`}>
                            <EventIcon className="w-3 h-3" />
                          </div>
                          
                          <div className="bg-secondary/20 rounded p-3 border border-border/50">
                            <div className="flex items-start justify-between mb-1">
                              <span className={`font-medium text-sm ${eventInfo.color}`}>
                                {eventInfo.label}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {formatDate(event.timestamp)}
                              </span>
                            </div>
                            
                            {/* Event details */}
                            <div className="text-sm text-muted-foreground">
                              {event.event_type === "created" && (
                                <p>Tool registered by {event.actor_name || "User"}</p>
                              )}
                              
                              {event.event_type === "status_changed" && (
                                <p>
                                  Status changed from <Badge variant="outline" className="text-xs mx-1">{event.details?.old_status}</Badge> 
                                  to <Badge variant="outline" className="text-xs mx-1">{event.details?.new_status}</Badge>
                                  {event.actor_type === "admin" && <span className="text-yellow-400 ml-1">(Admin)</span>}
                                </p>
                              )}
                              
                              {event.event_type === "details_updated" && event.details?.changes && (
                                <div>
                                  <p className="mb-1">Details updated by {event.actor_name || "User"}:</p>
                                  <ul className="list-disc list-inside text-xs space-y-0.5">
                                    {Object.entries(event.details.changes).map(([field, change]) => (
                                      <li key={field}>
                                        <span className="capitalize">{field.replace(/_/g, ' ')}</span>: 
                                        <span className="text-red-400/70 line-through mx-1">{change.old || 'empty'}</span>
                                        → <span className="text-green-400">{change.new || 'empty'}</span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {event.event_type === "ownership_transferred" && (
                                <p>
                                  Ownership transferred 
                                  {event.details?.new_owner_name && <span> to {event.details.new_owner_name}</span>}
                                  {event.details?.reason === "admin_transfer" && <span className="text-yellow-400 ml-1">(Admin Transfer)</span>}
                                  {event.details?.reason === "re_registration" && <span className="text-blue-400 ml-1">(Re-registration)</span>}
                                </p>
                              )}
                            </div>
                            
                            {event.actor_name && event.event_type !== "created" && event.event_type !== "details_updated" && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                                <User className="w-3 h-3" /> by {event.actor_name}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>

        {/* Image Viewer Modal */}
        <Dialog open={showImageViewer} onOpenChange={setShowImageViewer}>
          <DialogContent className="max-w-4xl w-[95vw] h-[85vh] p-0 bg-black/95 border-border overflow-hidden">
            <div className="absolute top-3 left-3 z-10 flex gap-2">
              <Button size="sm" variant="secondary" onClick={handleZoomIn} title="Zoom In">
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="secondary" onClick={handleZoomOut} title="Zoom Out">
                <ZoomOut className="w-4 h-4" />
              </Button>
              <Button size="sm" variant="secondary" onClick={handleResetZoom} title="Reset">
                <RotateCcw className="w-4 h-4" />
              </Button>
              <span className="flex items-center px-2 text-sm text-white bg-black/50 rounded">
                {Math.round(zoomLevel * 100)}%
              </span>
            </div>
            <div 
              className="w-full h-full flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing touch-none"
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              onWheel={handleWheel}
            >
              {toolImages.length > 0 && !imgError && (
                <img 
                  src={toolImages[activeImageIdx]?.url || imageUrl} 
                  alt={toolImages[activeImageIdx]?.label || `${tool.manufacturer} ${tool.tool_type}`}
                  className="select-none object-contain"
                  style={{
                    maxWidth: zoomLevel === 1 ? '100%' : 'none',
                    maxHeight: zoomLevel === 1 ? '100%' : 'none',
                    width: zoomLevel > 1 ? 'auto' : undefined,
                    height: zoomLevel > 1 ? 'auto' : undefined,
                    transform: `scale(${zoomLevel}) translate(${panPosition.x / zoomLevel}px, ${panPosition.y / zoomLevel}px)`,
                    transition: isDragging ? 'none' : 'transform 0.2s ease-out'
                  }}
                  draggable={false}
                  onError={() => setImgError(true)}
                />
              )}
            </div>
            <div className="absolute bottom-3 left-3 right-3">
              {toolImages.length > 1 && (
                <div className="flex justify-center gap-2 mb-2">
                  {toolImages.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => { setActiveImageIdx(idx); handleResetZoom(); }}
                      className={`px-3 py-1 rounded text-xs font-bold transition-colors ${
                        activeImageIdx === idx ? "bg-primary text-primary-foreground" : "bg-white/20 text-white/70 hover:bg-white/30"
                      }`}
                    >
                      {img.label}
                    </button>
                  ))}
                </div>
              )}
              <p className="text-sm text-white/70 text-center">
                Scroll to zoom · Drag to pan when zoomed · Click outside to close
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Recovery Dialog */}
        <Dialog open={showRecoverDialog} onOpenChange={setShowRecoverDialog}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase flex items-center gap-2">
                <PartyPopper className="w-5 h-5 text-green-500" />
                Mark Tool as Recovered
              </DialogTitle>
              <DialogDescription>
                Great news! If you've recovered your stolen tool, mark it here to update its status back to Active.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                <p className="text-sm text-green-400">
                  ✓ This tool will be marked as <strong>Active</strong> again
                </p>
                <p className="text-sm text-green-400 mt-1">
                  ✓ Recovery will be logged in the tool's history
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="recovery-notes">Recovery Notes (Optional)</Label>
                <Textarea
                  id="recovery-notes"
                  value={recoveryNotes}
                  onChange={(e) => setRecoveryNotes(e.target.value)}
                  placeholder="e.g., Found at pawn shop, Returned by police, Located via tracking..."
                  className="bg-background border-input min-h-[80px]"
                  rows={3}
                  data-testid="recovery-notes-input"
                />
                <p className="text-xs text-muted-foreground">
                  Add details about how you recovered the tool for your records.
                </p>
              </div>
            </div>
            
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setShowRecoverDialog(false);
                  setRecoveryNotes("");
                }}
                className="w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button 
                className="w-full sm:w-auto bg-green-600 hover:bg-green-700 text-white"
                onClick={handleRecover}
                disabled={recovering}
                data-testid="confirm-recovery-btn"
              >
                {recovering ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing...</>
                ) : (
                  <><PartyPopper className="w-4 h-4 mr-2" /> Confirm Recovery</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
